import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent } from 'react';

import { api, ApiError, type GenerationJob } from '../lib/api.js';
import { mediaOfJob, type PublishableMedia, type StudioHandoff } from '../lib/studioProject.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';
import { PublishMenu } from './PublishMenu.js';
import { isUnsettled, ResultCard } from './ResultCard.js';
import { GoalCompose } from './StudioGoalCompose.js';
import { ProjectArchive } from './StudioProjects.js';
import { UsagePanel } from './StudioUsage.js';
import { VideoPlanEditor, VideoPlanForm } from './VideoPlanPanel.js';

type StudioTab = 'compose' | 'library' | 'usage';
type ComposeMode = 'goal' | 'free';

/**
 * The content generation studio: an idea and some reference files go in,
 * a script/audio/video/image comes out, and an approved result can be handed
 * straight to the publish composer.
 *
 * One idea drives up to four chained requests — script, then audio, then
 * video or image — rather than four unrelated forms, because that is the
 * order the product spec describes the work happening in: a script is
 * reviewed before it is voiced, and a voice is reviewed before it is put on
 * a face.
 *
 * Everything one idea makes belongs to one project, which is what the archive
 * lists.
 *
 * The compose tab opens on a goal (a post, an image prompt, a video script…)
 * and lets the platform pick the connectors; the manual one-step-at-a-time
 * flow described above stays as "free mode".
 */
export function StudioPanel({
  onSendToPublish,
  onOpenScheduled,
  onOpenConnectors,
}: {
  onSendToPublish: (handoff: StudioHandoff) => void;
  /** Jump to the scheduled posts, for links from a project's publications. */
  onOpenScheduled?: () => void;
  /** Jump to the connector settings, for the warning about a step with no connector. */
  onOpenConnectors?: () => void;
}) {
  const [tab, setTab] = useState<StudioTab>('compose');
  const { t } = useI18n();

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t('studio.title')}</h2>
        <div className="segmented">
          <button
            type="button"
            className={tab === 'compose' ? 'active' : ''}
            onClick={() => setTab('compose')}
          >
            {t('studio.tab.compose')}
          </button>
          <button
            type="button"
            className={tab === 'library' ? 'active' : ''}
            onClick={() => setTab('library')}
          >
            {t('studio.tab.library')}
          </button>
          <button
            type="button"
            className={tab === 'usage' ? 'active' : ''}
            onClick={() => setTab('usage')}
          >
            {t('studio.tab.usage')}
          </button>
        </div>
      </div>

      {tab === 'compose' && (
        <ComposeTab
          onSendToPublish={onSendToPublish}
          onOpenScheduled={onOpenScheduled}
          onOpenConnectors={onOpenConnectors}
        />
      )}
      {tab === 'library' && (
        <ProjectArchive onSendToPublish={onSendToPublish} onOpenScheduled={onOpenScheduled} />
      )}
      {tab === 'usage' && <UsagePanel />}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Compose                                                                    */
/* -------------------------------------------------------------------------- */

/** Guided (goal first) by default; free mode is one click away. */
function ComposeTab({
  onSendToPublish,
  onOpenScheduled,
  onOpenConnectors,
}: {
  onSendToPublish: (handoff: StudioHandoff) => void;
  onOpenScheduled?: () => void;
  onOpenConnectors?: () => void;
}) {
  const { t } = useI18n();
  const [mode, setMode] = useState<ComposeMode>('goal');

  return (
    <>
      <div className="compose-mode">
        <div className="segmented" role="group" aria-label={t('studio.tab.compose')}>
          <button type="button" className={mode === 'goal' ? 'active' : ''} onClick={() => setMode('goal')}>
            {t('studio.mode.goal')}
          </button>
          <button type="button" className={mode === 'free' ? 'active' : ''} onClick={() => setMode('free')}>
            {t('studio.mode.free')}
          </button>
        </div>
        {mode === 'free' && <p className="muted small">{t('studio.mode.freeHint')}</p>}
      </div>

      {mode === 'goal' ? (
        <GoalCompose
          onSendToPublish={onSendToPublish}
          onOpenScheduled={onOpenScheduled}
          onOpenConnectors={onOpenConnectors}
        />
      ) : (
        <FreeCompose onSendToPublish={onSendToPublish} onOpenScheduled={onOpenScheduled} />
      )}
    </>
  );
}

type Attachment = { id: string; filename: string; mimeType: string; uploading?: boolean };

function FreeCompose({
  onSendToPublish,
  onOpenScheduled,
}: {
  onSendToPublish: (handoff: StudioHandoff) => void;
  onOpenScheduled?: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const [idea, setIdea] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pasteMode, setPasteMode] = useState(false);
  const [pastedScript, setPastedScript] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);

  // One request in flight at a time, per capability. Revising replaces the id
  // here with the new job's — that job is a fresh row with `parentJobId` set,
  // not an edit of the old one, so "current" always means "the latest one".
  const [scriptJobId, setScriptJobId] = useState<string | null>(null);
  const [audioJobId, setAudioJobId] = useState<string | null>(null);
  const [videoJobId, setVideoJobId] = useState<string | null>(null);
  const [imageJobId, setImageJobId] = useState<string | null>(null);
  const [includeAudioInVideo, setIncludeAudioInVideo] = useState(true);

  // A longer video is a plan rather than a job: the form asks for the shot
  // list, and the editor takes over once one exists.
  const [planFormOpen, setPlanFormOpen] = useState(false);
  const [videoPlanId, setVideoPlanId] = useState<string | null>(null);

  /*
   * The project this request belongs to. The first generation creates it
   * (from the idea, with its reference files) and every later one — audio,
   * video, image, revisions, video plans — passes its id, so one request
   * stays one project. The promise is kept, not only the id, so two
   * generations started in the same breath (a video and an image from a
   * pasted script) wait for the same project instead of opening two.
   * "New request" lets go of it.
   */
  const [projectId, setProjectId] = useState<string | null>(null);
  const projectPromise = useRef<Promise<string> | null>(null);

  const ensureProject = (): Promise<string> => {
    if (!projectPromise.current) {
      const created = api
        .createGenerationProject({
          requestText: idea.trim() || pastedScript.trim(),
          referenceMediaIds: attachments.filter((a) => !a.uploading).map((a) => a.id),
        })
        .then((project) => {
          setProjectId(project.id);
          void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
          return project.id;
        });
      // A failed creation must not poison the retry.
      created.catch(() => {
        if (projectPromise.current === created) projectPromise.current = null;
      });
      projectPromise.current = created;
    }
    return projectPromise.current;
  };

  const startNewRequest = () => {
    projectPromise.current = null;
    setProjectId(null);
    setIdea('');
    setAttachments([]);
    setPasteMode(false);
    setPastedScript('');
    setUploadError(null);
    setScriptJobId(null);
    setAudioJobId(null);
    setVideoJobId(null);
    setImageJobId(null);
    setIncludeAudioInVideo(true);
    setPlanFormOpen(false);
    setVideoPlanId(null);
  };

  // The same account/usage endpoint every other quota display reads from —
  // GENERATION_ATTACHMENTS_MAX is priced per plan but never counted server
  // side (it caps one request, not a running total), so the count compared
  // against the limit is the local attachment list's own length.
  const account = useQuery({ queryKey: ['account'], queryFn: api.account });
  const attachmentLimit = account.data?.usage['GENERATION_ATTACHMENTS_MAX']?.limit ?? null;
  const atCap = attachmentLimit !== null && attachments.length >= attachmentLimit;

  const scriptJob = useJobPoll(scriptJobId);
  const audioJob = useJobPoll(audioJobId);
  const videoJob = useJobPoll(videoJobId);
  const imageJob = useJobPoll(imageJobId);

  const effectiveScript = pasteMode ? pastedScript : (scriptJob.data?.resultText ?? '');

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = '';
    if (files.length === 0) return;
    setUploadError(null);

    for (const file of files) {
      if (attachmentLimit !== null && attachments.length >= attachmentLimit) break;
      const placeholder: Attachment = {
        id: `pending-${crypto.randomUUID()}`,
        filename: file.name,
        mimeType: file.type,
        uploading: true,
      };
      setAttachments((current) => [...current, placeholder]);
      try {
        const { asset } = await api.uploadGenerationMedia(file);
        setAttachments((current) =>
          current.map((entry) =>
            entry.id === placeholder.id
              ? { id: asset.id, filename: asset.filename, mimeType: asset.mimeType }
              : entry,
          ),
        );
      } catch (cause) {
        setAttachments((current) => current.filter((entry) => entry.id !== placeholder.id));
        setUploadError(cause instanceof ApiError ? cause.message : t('studio.uploadFailed'));
      }
    }
  };

  /** Every generation joins the request's project, and the archive learns of it. */
  const joined = (job: GenerationJob) => {
    void queryClient.invalidateQueries({ queryKey: ['generation-projects'] });
    void queryClient.invalidateQueries({ queryKey: ['generation-project', job.projectId] });
    return job;
  };

  const generateScript = useMutation({
    mutationFn: async () =>
      api.createGenerationJob({
        capability: 'SCRIPT',
        promptText: idea,
        referenceMediaIds: attachments.filter((a) => !a.uploading).map((a) => a.id),
        projectId: await ensureProject(),
      }),
    onSuccess: (job) => setScriptJobId(joined(job).id),
  });

  const generateAudio = useMutation({
    mutationFn: async () =>
      api.createGenerationJob({
        capability: 'AUDIO',
        promptText: effectiveScript,
        projectId: await ensureProject(),
      }),
    onSuccess: (job) => setAudioJobId(joined(job).id),
  });

  const generateVideo = useMutation({
    mutationFn: async () =>
      api.createGenerationJob({
        capability: 'VIDEO',
        promptText: effectiveScript,
        audioMediaId:
          includeAudioInVideo && audioJob.data?.resultMediaId
            ? audioJob.data.resultMediaId
            : undefined,
        projectId: await ensureProject(),
      }),
    onSuccess: (job) => setVideoJobId(joined(job).id),
  });

  const generateImage = useMutation({
    mutationFn: async () =>
      api.createGenerationJob({
        capability: 'IMAGE',
        promptText: effectiveScript || idea,
        projectId: await ensureProject(),
      }),
    onSuccess: (job) => setImageJobId(joined(job).id),
  });

  const canGenerateScript = idea.trim().length > 0 && !attachments.some((a) => a.uploading);
  const canGenerateFromScript = effectiveScript.trim().length > 0;

  // The words that would go with a picture or video into a post: the script
  // as it stands now (the latest revision, or what was pasted).
  const postText = effectiveScript.trim() || null;
  const menuFor = (job: GenerationJob | undefined) => {
    const file = job ? mediaOfJob(job) : null;
    const files: PublishableMedia[] = file ? [file] : [];
    return (
      <PublishMenu
        projectId={projectId}
        message={postText}
        media={files}
        onNewPost={onSendToPublish}
        onOpenScheduled={onOpenScheduled}
      />
    );
  };

  return (
    <>
      {projectId && (
        <div className="notice project-saved">
          <span>{t('studio.project.savedNote')}</span>
          <button type="button" className="ghost small" onClick={startNewRequest}>
            <Icon.plus />
            {t('studio.project.newRequest')}
          </button>
        </div>
      )}

      <label htmlFor="studio-idea">{t('studio.idea')}</label>
      <textarea
        id="studio-idea"
        rows={5}
        value={idea}
        onChange={(event) => setIdea(event.target.value)}
        placeholder={t('studio.ideaPlaceholder')}
      />

      <div className="studio-attachments">
        <div className="usage-head">
          <span>{t('studio.attachments')}</span>
          <span className="mono small">
            {attachmentLimit === null
              ? `${attachments.length} / ∞`
              : `${attachments.length} / ${attachmentLimit}`}
          </span>
        </div>

        {attachments.length > 0 && (
          <ul className="asset-list">
            {attachments.map((attachment) => (
              <li key={attachment.id}>
                <AttachmentPreview attachment={attachment} />
                <span className="small">{attachment.filename}</span>
                {attachment.uploading ? (
                  <span className="tiny muted">{t('studio.uploading')}</span>
                ) : (
                  <button
                    type="button"
                    className="ghost small"
                    onClick={() =>
                      setAttachments((current) => current.filter((a) => a.id !== attachment.id))
                    }
                  >
                    {t('common.remove')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {uploadError && <p className="error small">{uploadError}</p>}

        <input
          type="file"
          accept="image/*,video/*,audio/*"
          multiple
          disabled={atCap}
          onChange={upload}
        />
        {atCap && <p className="muted small">{t('studio.attachmentsCapped')}</p>}
      </div>

      <label className="check-row">
        <input
          type="checkbox"
          checked={pasteMode}
          onChange={(event) => setPasteMode(event.target.checked)}
        />
        <span className="tiny">{t('studio.pasteScriptToggle')}</span>
      </label>

      {pasteMode ? (
        <>
          <label htmlFor="studio-pasted-script">{t('studio.pastedScript')}</label>
          <textarea
            id="studio-pasted-script"
            rows={6}
            value={pastedScript}
            onChange={(event) => setPastedScript(event.target.value)}
            placeholder={t('studio.pastedScriptPlaceholder')}
          />
        </>
      ) : (
        <div className="actions">
          <button
            type="button"
            onClick={() => generateScript.mutate()}
            disabled={!canGenerateScript || generateScript.isPending || isUnsettled(scriptJob.data?.status ?? 'DONE')}
          >
            <Icon.sparkle />
            {t('studio.generateScript')}
          </button>
        </div>
      )}

      {generateScript.isError && <ErrorNotice error={generateScript.error} />}

      {!pasteMode && scriptJobId && (
        <ResultCard
          title={t('studio.scriptResult')}
          job={scriptJob.data}
          projectId={projectId}
          onRevised={(job) => setScriptJobId(job.id)}
          publishMenu={menuFor(undefined)}
        />
      )}

      {canGenerateFromScript && (
        <>
          <h3 style={{ marginTop: 22 }}>{t('studio.audioSection')}</h3>
          <div className="actions">
            <button
              type="button"
              onClick={() => generateAudio.mutate()}
              disabled={generateAudio.isPending || isUnsettled(audioJob.data?.status ?? 'DONE')}
            >
              <Icon.sparkle />
              {t('studio.generateAudio')}
            </button>
          </div>
          {generateAudio.isError && <ErrorNotice error={generateAudio.error} />}
          {audioJobId && (
            <ResultCard
              title={t('studio.audioResult')}
              job={audioJob.data}
              projectId={projectId}
              onRevised={(job) => setAudioJobId(job.id)}
            />
          )}

          <h3 style={{ marginTop: 22 }}>{t('studio.videoSection')}</h3>
          {audioJob.data?.status === 'DONE' || audioJob.data?.status === 'APPROVED' ? (
            <label className="check-row">
              <input
                type="checkbox"
                checked={includeAudioInVideo}
                onChange={(event) => setIncludeAudioInVideo(event.target.checked)}
              />
              <span className="tiny">{t('studio.includeAudio')}</span>
            </label>
          ) : null}
          <div className="actions">
            <button
              type="button"
              onClick={() => generateVideo.mutate()}
              disabled={generateVideo.isPending || isUnsettled(videoJob.data?.status ?? 'DONE')}
            >
              <Icon.sparkle />
              {t('studio.generateVideo')}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => generateImage.mutate()}
              disabled={generateImage.isPending || isUnsettled(imageJob.data?.status ?? 'DONE')}
            >
              <Icon.image />
              {t('studio.generateImage')}
            </button>
          </div>
          {generateVideo.isError && <ErrorNotice error={generateVideo.error} />}
          {generateImage.isError && <ErrorNotice error={generateImage.error} />}

          {!planFormOpen && !videoPlanId && (
            <div className="actions" style={{ marginTop: 6 }}>
              <button type="button" className="ghost" onClick={() => setPlanFormOpen(true)}>
                <Icon.list />
                {t('videoPlan.open')}
              </button>
            </div>
          )}
          {planFormOpen && !videoPlanId && (
            <VideoPlanForm
              scriptJobId={pasteMode ? null : scriptJobId}
              scriptText={effectiveScript}
              referenceMediaIds={attachments
                .filter((a) => !a.uploading && a.mimeType.startsWith('image/'))
                .map((a) => a.id)}
              ensureProjectId={ensureProject}
              onCreated={(plan) => {
                setVideoPlanId(plan.id);
                setPlanFormOpen(false);
              }}
              onCancel={() => setPlanFormOpen(false)}
            />
          )}
          {videoPlanId && (
            <VideoPlanEditor
              planId={videoPlanId}
              onBack={() => {
                setVideoPlanId(null);
                setPlanFormOpen(false);
              }}
            />
          )}
          {videoJobId && (
            <ResultCard
              title={t('studio.videoResult')}
              job={videoJob.data}
              projectId={projectId}
              onRevised={(job) => setVideoJobId(job.id)}
              publishMenu={menuFor(videoJob.data)}
            />
          )}
          {imageJobId && (
            <ResultCard
              title={t('studio.imageResult')}
              job={imageJob.data}
              projectId={projectId}
              onRevised={(job) => setImageJobId(job.id)}
              publishMenu={menuFor(imageJob.data)}
            />
          )}
        </>
      )}
    </>
  );
}

function AttachmentPreview({ attachment }: { attachment: Attachment }) {
  if (attachment.uploading) return <Icon.clock />;
  if (attachment.mimeType.startsWith('image/')) return <Icon.image />;
  if (attachment.mimeType.startsWith('video/')) return <Icon.send />;
  return <Icon.list />;
}

/** Poll a job while it is still moving, the same rule JobsPanel uses for its list. */
function useJobPoll(jobId: string | null) {
  return useQuery({
    queryKey: ['generation-job', jobId],
    queryFn: () => api.getGenerationJob(jobId!),
    enabled: jobId !== null,
    refetchInterval: (query) => (query.state.data && isUnsettled(query.state.data.status) ? 2_000 : false),
  });
}
